/**
 * Pot Results
 * How the last game's pot was paid out, pot by pot. With all-ins the pot
 * splits into a main pot and side pots, and each can only be won by the
 * players who put in enough to reach it — so a weaker hand can win a side
 * pot that a stronger hand (all-in for less) couldn't reach. Listing the
 * pots separately, with who could win each, makes that visible instead of
 * looking like the wrong hand won.
 */

import React from 'react';
import { PotResult } from '../../store/types';
import { formatChips } from '../../utils/format';

interface PotResultsProps {
  pots: PotResult[];
  /** The viewer's id, shown as "You". */
  youId?: string;
}

const PotResults: React.FC<PotResultsProps> = ({ pots, youId }) => {
  const name = (userId: string, username: string) => (userId === youId ? 'You' : username);
  // The main pot is open to everyone still in the game — compare against
  // that, not the table's current seats (eliminated players may have
  // already left by the time this shows).
  const everyoneCount = pots[0]?.eligible.length ?? 0;

  const whoCouldWin = (pot: PotResult) => {
    if (pot.eligible.length >= everyoneCount) return 'everyone';
    if (pot.eligible.length <= 3) return pot.eligible.map(p => name(p.userId, p.username)).join(' & ');
    return `${pot.eligible.length} players`;
  };

  const winnerLine = (
    icon: string,
    label: string,
    winners: PotResult['rankWinners'],
    tone: string,
  ) =>
    winners.length > 0 && (
      <div className="flex items-baseline gap-1.5 text-[11px] leading-snug">
        <span className="shrink-0" title={label}>{icon}</span>
        <span className="min-w-0">
          {winners.map((w, i) => (
            <span key={w.userId}>
              {i > 0 && <span className="text-gray-500"> & </span>}
              <span className={`font-semibold ${w.userId === youId ? 'text-yellow-300' : 'text-white'}`}>
                {name(w.userId, w.username)}
              </span>
              <span className={tone}> {w.handDescription}</span>
              <span className="text-yellow-400"> +{formatChips(w.amount)}</span>
            </span>
          ))}
        </span>
      </div>
    );

  return (
    <div data-testid="pot-results" className="p-2 sm:p-3 bg-gray-900/60 rounded-xl border border-gray-700 space-y-1.5">
      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
        {pots.length > 1 ? 'Pots' : 'Pot'}
      </div>
      {pots.map((pot, i) => (
        <div key={i} data-testid="pot-result" className="border-t border-gray-800 first:border-t-0 pt-1.5 first:pt-0">
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="font-semibold text-gray-200">
              {i === 0 ? 'Main pot' : `Side pot ${i}`}
              {pots.length > 1 && <span className="font-normal text-gray-500"> · {whoCouldWin(pot)}</span>}
            </span>
            <span className="text-yellow-400 font-bold shrink-0">{formatChips(pot.amount)}</span>
          </div>
          {winnerLine('🏆', 'Best Hand', pot.rankWinners, 'text-gray-300')}
          {winnerLine('💎', 'Best Colour', pot.jewelWinners, 'text-fuchsia-300')}
        </div>
      ))}
    </div>
  );
};

export default PotResults;
